import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useSites from './useSites';
import { sitesService, siteColour } from '../../_services/sites.service';
import { baseSalonIdOf, billingRow, errorText } from './addLocation/locationCopy';
import { sortLocationPayment } from './LocationBillingNag';
import './multiLocation.css';

/**
 * Billing → "Locations on your plan": one line per location, priced from the
 * salon plan quote (never a figure of our own). A trial line says when the
 * trial covers it until; a location with its own subscription says "Own
 * plan". A site added before billing was enforced, with nothing paying for
 * it, says "Not on your plan yet" and can be added from here: the same plan
 * step as adding a location, for that salon, creating nothing.
 * Single-site salons see nothing.
 */
export default function BillingLocations({ bare = false } = {}) {
    const history = useHistory();
    const { multi, sites, group, canAddLocation } = useSites();
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!multi || !canAddLocation) return undefined;
        let alive = true;
        sitesService.locationQuote().then((q) => { if (alive) setQuote(q || null); }).catch(() => { /* lines show without a price */ });
        return () => { alive = false; };
    }, [multi, canAddLocation]);

    if (!multi) return null;
    const baseId = baseSalonIdOf(group, sites);
    // Only the group's own sites are on this owner's plan (not a salon they
    // happen to work at too).
    const groupIds = group && Array.isArray(group.salonIds) && group.salonIds.length
        ? new Set(group.salonIds.map((x) => String(x && x._id ? x._id : x)))
        : null;
    const onPlan = groupIds ? sites.filter((s) => groupIds.has(String(s.salonId))) : sites;

    const addToPlan = async (s) => {
        if (busy) return;
        setBusy(String(s.salonId));
        setError('');
        try {
            // Its own open intent (one per site), so it never touches a new
            // location that's part-way through with Bailey.
            await sitesService.saveLocationIntent({ existingSalonId: s.salonId });
            history.push(`/add-location/plan?existingSalonId=${encodeURIComponent(s.salonId)}`);
        } catch (e) {
            setError(errorText(e));
            setBusy(null);
        }
    };

    const sortPayment = async (s) => {
        if (busy) return;
        setBusy(String(s.salonId));
        setError('');
        try {
            await sortLocationPayment(history, s);
        } catch (e) {
            setError(errorText(e));
        }
        setBusy(null);
    };

    // `bare`: just the lines, for a page that draws its own section
    // (Plan & billing). Without it, exactly the Billing card as before.
    return (
        <div className={bare ? 'ml-billing-bare' : 'settings-card'}>
            {!bare && <div className="settings-card-header"><h2>Locations on your plan</h2><p>Each location is on the Beu salon plan, as its own line.</p></div>}
            <div className="ml-site-list">
                {onPlan.map((s) => {
                    const row = billingRow(s, { quote, baseSalonId: baseId });
                    const isBusy = busy === String(s.salonId);
                    return (
                        <div key={String(s.salonId)} className="ml-site-row">
                            <span className="ml-dot" style={{ background: siteColour(s.salonId) }} />
                            <div>
                                <div className="ml-site-row-name">{s.salonName}</div>
                                <div className="ml-site-row-sub" style={row.kind === 'unbilled' || row.kind === 'lapsed' ? { color: '#B54708' } : undefined}>{row.text}</div>
                            </div>
                            {row.canSort && canAddLocation && (
                                <div className="ml-site-row-actions">
                                    <button type="button" className="bu-inline-save" onClick={() => sortPayment(s)} disabled={!!busy}>{isBusy ? 'Opening…' : 'Sort payment'}</button>
                                </div>
                            )}
                            {row.canAdd && canAddLocation && (
                                <div className="ml-site-row-actions">
                                    <button type="button" className="bu-inline-save" onClick={() => addToPlan(s)} disabled={!!busy}>{isBusy ? 'Opening…' : 'Add to plan'}</button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {error && <p className="ml-wizard-error" role="alert">{error}</p>}
        </div>
    );
}
